<template>
  <div>
    <base-modal
      :showing="visible"
      :size="size"
      @close="onClose"
      @opened="onOpened"
    >
      <base-card
        title="Detail Laporan"
        description="Data laporan yang telah dibuat"
        :with-style="false"
      >
        <div class="space-y-6 border-t border-gray-200 py-5">
          <dl class="grid grid-cols-3 gap-6">
            <div class="w-full">
              <dt class="text-xs text-gray-700">No Laporan</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                {{ orderBundle.data.attributes.code }}
              </dd>
            </div>
            <div class="w-full">
              <dt class="text-xs text-gray-700">Tanggal Laporan</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                {{ orderBundle.data.attributes.createdAt | formatDate }}
              </dd>
            </div>
            <div class="w-full">
              <dt class="text-xs text-gray-700">Status</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                <base-badge :color="status.color">{{ status.text }}</base-badge>
              </dd>
            </div>
            <div class="w-full">
              <dt class="text-xs text-gray-700">Nama Laporan</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                {{ orderBundle.data.attributes.name }}
              </dd>
            </div>
            <div class="w-full">
              <dt class="text-xs text-gray-700">Deskripsi Laporan</dt>
              <dd class="mt-1 text-sm font-bold text-gray-900">
                {{ orderBundle.data.attributes.description }}
              </dd>
            </div>
            <div class="col-span-3 w-full">
              <dt class="text-xs text-gray-700">Detail Faktur Penjualan</dt>
              <dd class="mt-1">
                <datatable
                  :columns="[
                    { id: 'Nomor Faktur', name: 'Nomor Faktur' },
                    { id: 'Wilayah Harga', name: 'Wilayah Harga' },
                    { id: 'Total Faktur', name: 'Total Faktur' },
                    { id: 'Total Pembayaran', name: 'Total Pembayaran' },
                    ...(!withOrderPayments ? [] : [{ id: 'action', name: '' }])
                  ]"
                >
                  <template #tbody="{ classes }">
                    <template v-for="order in getIncludedByType(orderBundle, 'orders')">
                      <tr
                        :key="order.id"
                        :class="classes.tr"
                      >
                        <td :class="classes.td">
                          <p class="font-bold text-gray-900">
                            {{ order.attributes.origin_code }} /
                            {{ order.attributes.destination_code }}
                          </p>
                        </td>
                        <td :class="classes.td">
                          {{ order.attributes.area_name }}
                        </td>
                        <td :class="classes.td">
                          {{ order.attributes.item_count | toCurrency }}
                        </td>
                        <td :class="classes.td">
                          <p v-if="office.attributes.office_type === 'stockist'">{{
                            order.attributes.grand_total_report_price | toCurrency
                          }}</p>
                          <p v-else>{{ order.attributes.total_kp_paid | toCurrency }}</p>
                        </td>
                        <td v-if="withOrderPayments" :class="classes.td">
                          <button class="text-right ml-auto flex justify-end" @click="onToggleOrderPaymentDetail(order)">
                            <Icon :icon="orderPaymentDetailsOpen.includes(order.id) ? 'heroicons:chevron-up' : 'heroicons:chevron-down'" />
                          </button>
                        </td>
                      </tr>
                      <template v-if="withOrderPayments && orderPaymentDetailsOpen.includes(order.id)">
                        <tr v-for="paymentMethod in orderPaymentDetails[order.id] ?? []" :key="`${order.id}-${paymentMethod.id}`" class="bg-gray-50">
                          <td></td>
                          <td></td>
                          <td :class="[classes.td, 'text-xs']">
                            {{ paymentMethod.attributes.payment_method_type }}
                          </td>
                          <td :class="[classes.td, 'text-xs']">
                            {{ paymentMethod.attributes.cashier_payment_amount | toCurrency }}
                          </td>
                          <td></td>
                        </tr>
                      </template>
                    </template>
                  </template>

                  <template #tfoot="{ classes }">
                    <tr :class="[classes.tr, 'bg-gray-50']">
                      <td :class="classes.td" colspan="3">Total</td>
                      <td :class="classes.td">
                        {{
                          orderBundle.data.attributes.bundle_report_price
                            | toCurrency
                        }}
                      </td>
                      <td v-if="withOrderPayments"></td>
                    </tr>
                  </template>
                </datatable>
              </dd>
            </div>
            <div class="col-span-3 w-full">
              <div class="flex items-center justify-between">
                <dt class="text-xs text-gray-700">Detail Faktur Pembayaran</dt>
                <base-button
                  v-if="
                    !orderBundle.data.attributes.is_submitted ||
                    (forceDisplayAction && (!orderBundle.data.attributes.is_validated || !orderBundle.data.attributes.is_paid))
                  "
                  size="sm"
                  color="white"
                  @click="onOpenAddPaymentMethod"
                >
                  <Icon icon="heroicons:plus" />
                  Tambah Pembayaran
                </base-button>
              </div>
              <dd class="mt-2">
                <datatable
                  :columns="[
                    { id: 'payment_method', name: 'Metode Pembayaran' },
                    { id: 'amount', name: 'Jumlah Pembayaran' },
                    { id: 'status', name: 'Status', theadClass: 'text-center' },
                    ...(!orderBundle.data.attributes.is_submitted ||
                    forceDisplayAction
                      ? [
                          {
                            id: 'action',
                            name: 'Aksi',
                            theadClass: 'text-center',
                          },
                        ]
                      : []),
                  ]"
                >
                  <template #tbody="{ classes }">
                    <tr
                      v-for="payment in getIncludedByType(
                        orderBundle,
                        'payment-bundles'
                      )"
                      :key="payment.id"
                      :class="classes.tr"
                    >
                      <td :class="classes.td">
                        <p v-if="payment.attributes.payment_type === 'balance'">
                          Saldo Sisa Pembayaran
                        </p>
                        <p
                          v-else-if="
                            payment.attributes.office_bank_type === 'cash'
                          "
                        >
                          Cash
                        </p>
                        <p v-else>
                          Transfer Bank -
                          {{ payment.attributes.office_bank_name }}
                        </p>
                      </td>
                      <td :class="classes.td">
                        {{ payment.attributes.payment_amount | toCurrency }}
                      </td>
                      <td :class="[classes.td, 'text-center']">
                        <base-button
                          v-if="withVerify && !payment.attributes.is_verified"
                          size="sm"
                          @click="onVerifyPayment(payment)"
                          >Verifikasi</base-button
                        >
                        <base-badge
                          v-else
                          :color="
                            payment.attributes.is_verified ? 'green' : 'red'
                          "
                        >
                          {{
                            payment.attributes.is_verified
                              ? 'Terverifikasi'
                              : 'Belum Terverifikasi'
                          }}
                        </base-badge>
                      </td>
                      <td
                        v-if="
                          !orderBundle.data.attributes.is_submitted ||
                          (forceDisplayAction && !payment.attributes.is_verified)
                        "
                        :class="[classes.td, 'text-center']"
                      >
                        <div class="flex items-center justify-center gap-2">
                          <button @click="onEditPayment(payment)">
                            <Icon
                              icon="heroicons:pencil-square-solid"
                              class="h-4 w-4 text-green-600"
                            />
                          </button>
                          <button @click="onDeletePayment(payment)">
                            <Icon
                              icon="heroicons:trash-solid"
                              class="h-4 w-4 text-red-600"
                            />
                          </button>
                        </div>
                      </td>
                    </tr>
                  </template>
                </datatable>
              </dd>
            </div>
          </dl>
        </div>
        <div
          v-if="!orderBundle.data.attributes.is_submitted"
          class="flex justify-between"
        >
          <base-button color="danger" @click="cancelVisible = true">
            <Icon icon="heroicons:trash-solid" class="h-4 w-4" />
            Batal
          </base-button>
          <base-button @click="onSubmit">
            <Icon icon="heroicons:shield-check-solid" class="h-4 w-4" />
            Validasi
          </base-button>
        </div>
        <div
          v-if="withVerify && orderBundle.data.attributes.is_submitted && !orderBundle.data.attributes.is_validated"
          class="flex justify-end gap-2"
        >
          <base-button color="danger" @click="onCancelSubmit">
            <Icon icon="heroicons:x-mark" class="h-4 w-4" />
            Cancel
          </base-button>
          <template v-if="!orderBundle.data.attributes.is_validated && allPaymentValid">
            <base-button v-if="orderBundle.data.attributes.total_payment_verified < orderBundle.data.attributes.bundle_report_price" @click="onOpenAccAtasan">
              <Icon icon="heroicons:shield-check-solid" class="h-4 w-4" />
              Acc Atasan
            </base-button>
            <base-button v-else @click="onVerify">
              <Icon icon="heroicons:shield-check-solid" class="h-4 w-4" />
              Verifikasi
            </base-button>
          </template>
        </div>
      </base-card>
    </base-modal>

    <base-confirm
      @close="cancelVisible = false"
      @confirmed="onCancelConfirmed"
      size="max-w-xl"
      :icon="false"
      message="Ketik ulang kalimat berikut ini untuk mengkonfirmasi aksi pembatalan"
      :code="`Laporan dengan kode ${orderBundle.data.attributes.code} akan saya batalkan`"
      :center="false"
      input
      :visible="cancelVisible"
    />

    <base-confirm
      @close="accAtasan.visible = false"
      @confirmed="onVerify"
      icon-type="badge"
      title="Acc Atasan"
      :visible="accAtasan.visible"
    >
      <template #content>
        <base-input inset tag="textarea" rows="5" placeholder="Alasan ACC Atasan" v-model="accAtasan.description" />
      </template>
    </base-confirm>

    <base-modal
      :showing="addPaymentMethod.visible"
      @close="addPaymentMethod.visible = false"
    >
      <base-card
        title="Tambah Pembayaran"
        description="Tambah Metode Pembayaran"
        :with-style="false"
      >
        <div class="space-y-4 border-t pt-5">
          <create-payment-form
            sync
            cardless
            :extra-payment-method-types="[
              {
                key: 'overpayment_balance',
                value: 'overpayment_balance',
                label: `Saldo Sisa Pembayaran (${
                  office
                    ? $options.filters.toCurrency(
                        office.attributes.overpayment_balance
                      )
                    : '0'
                })`,
                disabled: false,
              },
            ]"
            :with-validate-button="false"
            :with-summary="false"
            :office-id="orderBundle.meta.pusat_uuid"
            v-model="addPaymentMethod.methods"
          />
          <div class="flex gap-2">
            <base-button
              :disabled="totalAddPaymentMethod < 1"
              @click="onSaveAddPaymentMethod"
              >Simpan</base-button
            >
            <base-button color="white" @click="addPaymentMethod.visible = false"
              >Batal</base-button
            >
          </div>
        </div>
      </base-card>
    </base-modal>

    <base-modal
      :showing="editPaymentMethod.visible"
      @close="editPaymentMethod.visible = false"
    >
      <base-card
        title="Edit Pembayaran"
        description="Edit Metode Pembayaran"
        :with-style="false"
      >
        <form
          class="space-y-4 border-t pt-5"
          @submit.prevent="onSaveEditPaymentMethod"
        >
          <div class="grid grid-cols-2 gap-4">
            <base-input
              inset
              label="Metode Pembayaran"
              :value="editPaymentMethod.type"
              disabled
            />
            <base-input
              inset
              label="Nominal"
              type="text"
              currency
              v-model="editPaymentMethod.amount"
            />
          </div>
          <div class="flex gap-2">
            <base-button>Simpan</base-button>
            <base-button
              type="button"
              color="white"
              @click="editPaymentMethod.visible = false"
              >Batal</base-button
            >
          </div>
        </form>
      </base-card>
    </base-modal>
    <loading v-if="loading" />
  </div>
</template>

<script>
import { requestMixin } from '@/mixins/request/request';
import BaseModal from '@/components/base/BaseModal.vue';
import BaseConfirm from '@/components/base/BaseConfirm.vue';
import CreatePaymentForm from '@/components/payment/create-payment-form.vue';
import { mapActions } from 'vuex';

export default {
  name: 'ViewOrderModal',
  mixins: [requestMixin],
  components: {
    BaseModal,
    BaseConfirm,
    CreatePaymentForm,
  },
  props: {
    size: {
      type: String,
      default: 'max-w-5xl',
    },
    visible: Boolean,
    orderBundleId: String,
    withVerify: Boolean,
    forceDisplayAction: Boolean,
    withOrderPayments: Boolean
  },
  emits: ['close', 'submitted', 'deleted', 'verified', 'canceled'],
  data() {
    return {
      cancelVisible: false,
      loadingCancel: false,
      loadingDetail: false,
      loadingSubmit: false,
      loadingVerify: false,
      loadingCancelSubmit: false,
      loadingVerifyPayment: false,
      loadingAddPaymentMethod: false,
      loadingOpenOrderPaymentDetails: false,
      orderBundle: {
        data: {
          attributes: {},
          relationships: {},
        },
        meta: {},
        included: [],
      },
      addPaymentMethod: {
        visible: false,
        methods: [
          {
            paymentMethodId: null,
            paymentMethodTypeId: null,
            amount: null,
            officeBankId: null,
            receiptNumber: null,
          },
        ],
      },
      editPaymentMethod: {
        id: null,
        visible: false,
        type: null,
        amount: null,
      },
      orderPaymentDetailsOpen: [],
      orderPaymentDetails: {},
      accAtasan: {
        description: null,
        visible: false
      }
    };
  },
  computed: {
    loading() {
      return (
        this.loadingDetail ||
        this.loadingCancel ||
        this.loadingSubmit ||
        this.loadingSubmit ||
        this.loadingVerify ||
        this.loadingVerifyPayment ||
        this.loadingAddPaymentMethod ||
        this.loadingCancelSubmit ||
        this.loadingOpenOrderPaymentDetails
      );
    },
    allPaymentValid() {
      return this.getIncludedByType(this.orderBundle, 'payment-bundles').every(
        (paymentBundle) => paymentBundle.attributes.is_verified
      );
    },
    office() {
      if (!this.orderBundle.data.relationships.office) {
        return null;
      }
      return this.getSingleIncluded(
        this.orderBundle,
        this.orderBundle.data.relationships.office.data.id
      );
    },
    status() {
      if (this.orderBundle.data.attributes.is_canceled) {
        return {
          color: 'red',
          text: 'Dibatalkan',
        };
      }

      if (this.orderBundle.data.attributes.is_validated) {
        return {
          color: 'green',
          text: 'Tervalidasi',
        };
      }

      return {
        color: 'yellow',
        text: 'Belum Tervalidasi',
      };
    },
    totalAddPaymentMethod() {
      return this.addPaymentMethod.methods.reduce(
        (total, method) => total + method.amount,
        0
      );
    },
  },
  methods: {
    ...mapActions({
      createAlert: 'alert/createAlert',
    }),
    async loadOrderBundle() {
      this.loadingDetail = true;

      const [res, err] = await this.request(
        `/api/v1/order-bundles/${this.orderBundleId}`,
        {
          params: {
            include: 'orders,paymentBundles,office',
            'fields[order-bundles]':
              'code,createdAt,is_validated,name,description,is_submitted,is_canceled,is_paid,orders,bundle_report_price,total_payment_verified,paymentBundles,office',
            'fields[orders]':
              'origin_code,destination_code,area_name,item_count,grand_total_report_price,total_kp_paid',
            'fields[payment-bundles]':
              'office_bank_type,office_bank_name,payment_amount,is_verified,payment_type',
            'fields[offices]': 'overpayment_balance,office_type',
          },
        }
      );

      if (!err) {
        this.orderBundle = res;
      }

      this.loadingDetail = false;
    },
    onOpenAddPaymentMethod() {
      this.addPaymentMethod.methods = [
        {
          paymentMethodId: null,
          paymentMethodTypeId: null,
          amount: null,
          officeBankId: null,
          receiptNumber: null,
        },
      ];
      this.addPaymentMethod.visible = true;
    },
    onOpenAccAtasan() {
      this.accAtasan.description = null
      this.accAtasan.visible = true
    },
    async onCancelConfirmed() {
      this.loadingCancel = true;

      const [, err] = await this.request(
        `/api/v1/order-bundles/${this.orderBundleId}`,
        {
          method: 'delete',
        }
      );

      if (!err) {
        this.cancelVisible = false;

        this.onClose();
        this.$emit('deleted');
      }

      this.loadingCancel = false;
    },
    onClose() {
      this.$emit('close');
    },
    async onOpened() {
      this.orderPaymentDetailsOpen = []
      this.orderPaymentDetails = {}

      this.loadOrderBundle();
    },
    async onSubmit() {
      this.loadingSubmit = true;

      const [, err] = await this.request(
        `/api/v1/order-bundles/${this.orderBundleId}`,
        {
          method: 'patch',
          data: {
            data: {
              type: 'order-bundles',
              id: this.orderBundleId,
              attributes: {
                is_submitted: true,
              },
            },
          },
        }
      );

      if (!err) {
        this.onClose();
        this.$emit('submitted');
      }

      this.loadingSubmit = false;
    },
    async onCancelSubmit() {
      this.loadingCancelSubmit = true;

      const [, err] = await this.request(
        `/api/v1/order-bundles/${this.orderBundleId}/-actions/cancel-order-bundle`,
        {
          method: 'patch',
        }
      );

      if (!err) {
        this.onClose();
        this.$emit('canceled');
      }

      this.loadingCancelSubmit = false;
    },
    async onVerify() {
      this.loadingVerify = true;

      const [, err] = await this.request(
        `/api/v1/order-bundles/${this.orderBundleId}/-actions/validate-order-bundle`,
        {
          method: 'patch',
          data: {
            acc_payment_description: this.accAtasan.description
          }
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });
      } else {
        this.accAtasan.description = null
        this.accAtasan.visible = false

        this.onClose();
        this.$emit('verified');
      }

      this.loadingVerify = false;
    },
    async onVerifyPayment(payment) {
      this.loadingVerifyPayment = true;

      const [, err] = await this.request(
        `/api/v1/payment-bundles/${payment.id}/-actions/verify`,
        {
          method: 'patch',
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });
      } else {
        this.loadOrderBundle();
      }

      this.loadingVerifyPayment = false;
    },
    async onSaveAddPaymentMethod() {
      this.loadingAddPaymentMethod = true;

      const [paymentMethod] = this.addPaymentMethod.methods;

      const [, err] = await this.request(`/api/v1/payment-bundles`, {
        method: 'post',
        data: {
          data: {
            type: 'payment-bundles',
            attributes: {
              payment_amount: paymentMethod.amount
                ? +paymentMethod.amount.replace(/\D/gi, '')
                : null,
            },
            relationships: {
              'order-bundle': {
                data: {
                  type: 'order-bundles',
                  id: this.orderBundleId,
                },
              },
              ...(paymentMethod.officeBankId
                ? {
                    'office-bank': {
                      data: {
                        type: 'office-banks',
                        id: paymentMethod.officeBankId,
                      },
                    },
                  }
                : {}),
            },
          },
        },
      });

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });
      } else {
        this.loadOrderBundle();
        this.addPaymentMethod.visible = false;
      }

      this.loadingAddPaymentMethod = false;
    },
    async onSaveEditPaymentMethod() {
      this.loadingAddPaymentMethod = true;

      const [, err] = await this.request(
        `/api/v1/payment-bundles/${this.editPaymentMethod.id}`,
        {
          method: 'patch',
          data: {
            data: {
              id: this.editPaymentMethod.id,
              type: 'payment-bundles',
              attributes: {
                payment_amount: this.editPaymentMethod.amount
                  ? +this.editPaymentMethod.amount.replace(/\D/gi, '')
                  : null,
              },
            },
          },
        }
      );

      if (err) {
        this.createAlert({
          status: 'error',
          data: this.getRequestErrorMessage(err),
        });
      } else {
        this.loadOrderBundle();
        this.editPaymentMethod.visible = false;
      }

      this.loadingAddPaymentMethod = false;
    },
    async onDeletePayment(paymentMethod) {
      this.loadingAddPaymentMethod = true;

      const [, err] = await this.request(
        `/api/v1/payment-bundles/${paymentMethod.id}`,
        {
          method: 'delete',
        }
      );

      if (!err) {
        this.loadOrderBundle();
      }

      this.loadingAddPaymentMethod = false;
    },
    onEditPayment(payment) {
      if (payment.attributes.payment_type === 'balance') {
        this.editPaymentMethod.type = `Saldo Sisa Pembayaran (${this.$options.filters.toCurrency(
          this.office.attributes.overpayment_balance
        )})`;
      } else if (payment.attributes.office_bank_type === 'cash') {
        this.editPaymentMethod.type = 'Cash';
      } else {
        this.editPaymentMethod.type = `Transfer Bank - ${payment.attributes.office_bank_name}`;
      }

      this.editPaymentMethod.id = payment.id;
      this.editPaymentMethod.amount = `${payment.attributes.payment_amount}`;

      this.editPaymentMethod.visible = true;
    },
    async onToggleOrderPaymentDetail(order) {
      if (this.orderPaymentDetailsOpen.includes(order.id)) {
        this.orderPaymentDetailsOpen = this.orderPaymentDetailsOpen.filter(id => order.id !== id)
      } else {
        if (this.orderPaymentDetails[order.id]) {
          this.orderPaymentDetailsOpen.push(order.id)
        } else {
          this.loadingOpenOrderPaymentDetails = true

          const [res, err] = await this.request(`/api/v1/orders/${order.id}/payment-methods`, {
            params: {
              'filter[canceled]': false
            }
          })

          if (!err) {
            this.orderPaymentDetails[order.id] = res.data
            this.orderPaymentDetailsOpen.push(order.id)
          }

          this.loadingOpenOrderPaymentDetails = false
        }
      }
    }
  },
};
</script>
